import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import Swal from 'sweetalert2'
import { Button, Modal, ModalBody } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrophy } from '@fortawesome/free-solid-svg-icons'
import 'animate.css'
import { formatMultiplier, getLevelStyle } from './rewardsUtils'
import { REWARDS_ROUTE } from './rewardsNotifications'
import { useRewardsNotificationSettings, useUnreadRewardsNotifications } from './useRewardsNotifications'

// HO-TASK-290: celebra los logros de Homelan Rewards apenas el propietario
// entra a la web: un modal al subir de nivel y un aviso breve, que no bloquea
// nada, al avanzar la racha. Los logros salen de las notificaciones que crea
// el backend (HO-TASK-278), así que respetan la preferencia de silenciarlas.
// El sorteo no se celebra todavía: depende de HO-TASK-273/274 (backlog).

// Pantallas de pago: una celebración ahí distrae de un pago en curso, así que
// se espera a que el propietario salga de ellas.
const PAYMENT_PATHS = /^\/dashboard\/my\/finance\/(topays?|advance)(\/|$)/

// Nombre del nivel (lo que trae la notificación) a su insignia.
const LEVEL_BADGES = { bronce: 'bronze', plata: 'silver', oro: 'gold' }

const MAX_REMEMBERED = 100

// Ids ya celebrados en este navegador: cada logro se celebra una sola vez,
// no en cada recarga.
const storageKeyFor = (userKey) => `rewards:celebrated:${userKey}`

const readCelebrated = (key) => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || '[]')
    return Array.isArray(stored) ? stored : []
  } catch (error) {
    return []
  }
}

const rememberCelebrated = (key, id) => {
  try {
    const next = [...readCelebrated(key).filter((item) => item !== id), id].slice(-MAX_REMEMBERED)
    window.localStorage.setItem(key, JSON.stringify(next))
  } catch (error) {
    // Sin almacenamiento el logro podría volver a celebrarse; no rompe nada.
  }
}

const StreakToast = Swal.mixin({
  toast: true,
  position: 'bottom-start',
  showConfirmButton: false,
  showCloseButton: true,
  timer: 6000
})

const LevelUpModal = ({ celebration, onClose }) => {
  const { level, coin_multiplier: multiplier } = celebration?.payload || {}
  const style = getLevelStyle(LEVEL_BADGES[String(level || '').toLowerCase()])

  return (
    <Modal isOpen={!!celebration} toggle={onClose} centered size="sm" aria-labelledby="rewards-level-up-title">
      <ModalBody className="text-center p-4">
        <div
          className="d-flex flex-center rounded-circle mx-auto mb-3 animate__animated animate__bounceIn"
          style={{ width: '5rem', height: '5rem', color: style.color, background: style.background }}
        >
          <FontAwesomeIcon icon={faTrophy} className="fs-4" />
        </div>
        <h4 id="rewards-level-up-title" className="mb-2">
          ¡Subiste a {level}!
        </h4>
        <p className="fs--1 text-700 mb-4">
          {Number(multiplier) > 1
            ? `Desde ahora cada pago puntual te da más Homelan Coins: se multiplican por ${formatMultiplier(multiplier)}.`
            : 'Sigue pagando a tiempo para ganar más Homelan Coins.'}
        </p>
        <Button tag={Link} to={REWARDS_ROUTE} color="primary" block onClick={onClose}>
          Ver mis Rewards
        </Button>
        <Button color="link" block className="mt-1" onClick={onClose}>
          Seguir
        </Button>
      </ModalBody>
    </Modal>
  )
}

const RewardsCelebrations = () => {
  const { pathname } = useLocation()
  const { role, property, hasMultipleProperties, token } = useSelector((state) => state.auth.user) || {}
  const { selectedProperty } = useSelector((state) => state.property)
  // Con varias unidades y ninguna elegida todavía no hay wallet activa.
  const active = role === 'property' && !!token && !(hasMultipleProperties && !selectedProperty)
  const propertyId = selectedProperty || property || null
  const storageKey = storageKeyFor(propertyId || 'me')

  const { enabled } = useRewardsNotificationSettings(active)
  const listen = active && enabled
  const levels = useUnreadRewardsNotifications('rewards-level', { active: listen, propertyId })
  const streaks = useUnreadRewardsNotifications('rewards-streak', { active: listen, propertyId })

  const [current, setCurrent] = useState(null)
  const [celebratedNow, setCelebratedNow] = useState([])
  // Hay una celebración en pantalla. Es estado y no ref: al cerrarse tiene que
  // volver a correr el efecto para mostrar la siguiente.
  const [busy, setBusy] = useState(false)

  // Pendientes, del más viejo al más nuevo.
  const pending = useMemo(() => {
    if (!listen) return []
    const done = new Set([...readCelebrated(storageKey), ...celebratedNow])
    return [...levels.items, ...streaks.items]
      .filter((notification) => !done.has(notification.id))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
  }, [listen, storageKey, levels.items, streaks.items, celebratedNow])

  const markDone = (id) => {
    rememberCelebrated(storageKey, id)
    setCelebratedNow((ids) => [...ids, id])
  }

  const next = pending[0]
  const blocked = PAYMENT_PATHS.test(pathname)

  useEffect(() => {
    if (!next || blocked || busy) return
    setBusy(true)
    // Se recuerda al mostrarlo, no al cerrarlo: si el propietario recarga con
    // la celebración abierta, no vuelve a aparecer.
    rememberCelebrated(storageKey, next.id)

    if (next.type === 'rewards-level') {
      setCurrent(next)
      return
    }

    StreakToast.fire({ type: 'success', title: `🔥 ${next.payload.message || '¡Tu racha de pagos puntuales avanzó!'}` }).then(() => {
      markDone(next.id)
      setBusy(false)
    })
  }, [next?.id, blocked, busy])

  const closeLevel = () => {
    if (current) markDone(current.id)
    setCurrent(null)
    setBusy(false)
  }

  if (!active) return null
  return <LevelUpModal celebration={current} onClose={closeLevel} />
}

export default RewardsCelebrations
